import { useRef, useState } from 'react';
import { FileUp, Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

type CsvUploadDialogProps = {
  open: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onSubmit: (file: File) => Promise<void> | void;
  isSubmitting: boolean;
};

const CsvUploadDialog = ({
  open,
  onOpenChange,
  onSubmit,
  isSubmitting,
}: CsvUploadDialogProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const isCsvFile = (candidate: File) => {
    const name = candidate.name.toLowerCase();
    return (
      name.endsWith('.csv') ||
      candidate.type === 'text/csv' ||
      candidate.type === 'application/vnd.ms-excel'
    );
  };

  const handleFile = (candidate: File | null) => {
    if (!candidate) return;
    if (!isCsvFile(candidate)) return;
    setFile(candidate);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        onOpenChange(isOpen);
        if (!isOpen && !isSubmitting) {
          setFile(null);
          setIsDragOver(false);
        }
      }}
    >
      <DialogContent className="sm:max-w-[940px] p-0 overflow-hidden">
        <DialogHeader className="border-b border-border/50 bg-muted/20 px-6 py-5">
          <DialogTitle className="text-2xl">Add users</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 px-6 py-5 lg:grid-cols-[1.2fr_1fr]">
          <div
            className={cn(
              'rounded-xl border border-dashed p-6 transition-colors',
              isDragOver
                ? 'border-primary bg-primary/5'
                : 'border-border/70 bg-card/50'
            )}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragOver(false);
              handleFile(event.dataTransfer.files?.[0] ?? null);
            }}
          >
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(event) =>
                handleFile(event.target.files?.[0] ?? null)
              }
            />

            <button
              type="button"
              className="flex w-full flex-col items-center justify-center gap-2 py-10 text-center"
              onClick={() => inputRef.current?.click()}
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <FileUp className="h-5 w-5 text-muted-foreground" />
              </div>
              <p className="text-sm font-semibold text-muted-foreground">
                Drag and drop files here
              </p>
              <p className="text-sm text-muted-foreground">
                Supported file type is CSV.
              </p>
              {file ? (
                <p className="mt-2 rounded-md bg-muted px-2 py-1 text-xs text-foreground">
                  {file.name}
                </p>
              ) : null}
            </button>
          </div>

          <div className="rounded-xl border border-border/70 bg-card/50 p-4">
            <p className="mb-3 text-sm font-semibold text-foreground">
              Example of file content
            </p>
            <img
              src="/images/static-images/example-users-school.png"
              alt="Example CSV content for bulk users upload"
              className="w-full rounded-md border border-border/60"
            />
          </div>
        </div>

        <div className="flex justify-end border-t border-border/50 px-6 py-4">
          <Button
            type="button"
            disabled={!file || isSubmitting}
            onClick={() => {
              if (!file || isSubmitting) return;
              void onSubmit(file);
            }}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <FileUp className="h-4 w-4" />
                Upload
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CsvUploadDialog;
